"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APosFragranceUpdateInputSchema = exports.APosFragranceCreateInputSchema = exports.APosFragranceSchema = exports.APosFragranceMarkupTierSchema = exports.APosFragranceScentFamilySchema = exports.APosFragranceNoteClassificationSchema = exports.APosFragranceCategorySchema = void 0;
const zod_1 = require("zod");
const timestamps_schema_1 = require("../../common/timestamps.schema");
const id_schema_1 = require("../../common/id.schema");
const oil_blending_stock_schema_1 = require("./oil-blending-stock.schema");
/**
 * Module 29 — Oil Blending
 * Fragrance catalog entry. PK = tenantId, SK = fragranceId (frag_{ulid12}).
 *
 * STOCK MODEL — 2026-06-04 redesign:
 * Stock now lives on this row in `stock_by_location` keyed by locationId.
 * Single canonical unit is mL — no per-tenant or per-fragrance unit toggle.
 * Fragrances are NOT Variants in the Products pipeline — they live entirely
 * in the oil-blending area (own table, own search index, never indexed as
 * a sellable variant).
 *
 * `linked_variant_id` is preserved as an optional legacy field for migration
 * purposes only; new records do not set it. The variant it points at (if any)
 * is dead weight — kept until the cleanup migration runs.
 */
exports.APosFragranceCategorySchema = zod_1.z.enum(['body_oil', 'essential_oil', 'both']);
exports.APosFragranceNoteClassificationSchema = zod_1.z.enum(['top', 'middle', 'base', 'unclassified']);
exports.APosFragranceScentFamilySchema = zod_1.z.enum([
    'citrus', 'floral', 'woody', 'oriental', 'fresh',
    'gourmand', 'earthy', 'spicy', 'musk', 'herbal',
]);
exports.APosFragranceMarkupTierSchema = zod_1.z.enum(['standard', 'premium', 'luxury']);
const decimal2dp = (val) => Number.isFinite(val) && Math.round(val * 100) / 100 === val;
const decimal3dp = (val) => Number.isFinite(val) && Math.round(val * 1000) / 1000 === val;
const decimal4dp = (val) => Number.isFinite(val) && Math.round(val * 10000) / 10000 === val;
exports.APosFragranceSchema = zod_1.z.object({
    tenantId: id_schema_1.TenantIdSchema,
    fragranceId: id_schema_1.EntityIdSchema.describe('Format: frag_{ulid12}'),
    name: zod_1.z.string().min(1).max(120),
    description: zod_1.z.string().max(2000).optional(),
    category: exports.APosFragranceCategorySchema,
    note_classification: exports.APosFragranceNoteClassificationSchema,
    note_auto_detected: zod_1.z.boolean().default(true),
    scent_family: exports.APosFragranceScentFamilySchema,
    // Required when category includes body_oil. 0–100, 2dp. Drives HARD safety block.
    skin_safe_max_percent: zod_1.z.number().min(0).max(100).refine(decimal2dp, 'must be 2dp').optional(),
    cost_per_ml: zod_1.z.number().nonnegative().refine(decimal4dp, 'must be 4dp'),
    markup_tier: exports.APosFragranceMarkupTierSchema.default('standard').optional(),
    markup_override: zod_1.z.number().nonnegative().optional(),
    reorder_threshold_ml: zod_1.z.number().nonnegative().refine(decimal3dp, 'must be 3dp'),
    supplier: zod_1.z.string().max(200).optional(),
    brand_id: id_schema_1.EntityIdSchema.optional(),
    notes: zod_1.z.string().max(2000).optional(),
    is_active: zod_1.z.boolean().default(true),
    is_visible_to_customers: zod_1.z.boolean().default(true),
    image_url: zod_1.z.string().url().optional(),
    // Optional barcode for receiving-scan identification only — NEVER scannable
    // for sale (POS register's variant lookup will not find it).
    internal_barcode: zod_1.z.string().max(64).optional(),
    // Stock keyed by locationId. Empty map = no stock anywhere yet.
    stock_by_location: oil_blending_stock_schema_1.APosIngredientStockByLocationSchema.default({}),
    // Legacy — kept for migration; new records do not set this.
    linked_variant_id: id_schema_1.EntityIdSchema.optional().describe('LEGACY — pre-2026-06-04 records only. Stock now lives on stock_by_location.'),
}).merge(timestamps_schema_1.TimestampsSchema);
exports.APosFragranceCreateInputSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).max(120),
    description: zod_1.z.string().max(2000).optional(),
    category: exports.APosFragranceCategorySchema,
    note_classification: exports.APosFragranceNoteClassificationSchema.optional(),
    scent_family: exports.APosFragranceScentFamilySchema,
    skin_safe_max_percent: zod_1.z.number().min(0).max(100).refine(decimal2dp, 'must be 2dp').optional(),
    cost_per_ml: zod_1.z.number().nonnegative().refine(decimal4dp, 'must be 4dp'),
    markup_tier: exports.APosFragranceMarkupTierSchema.optional(),
    markup_override: zod_1.z.number().nonnegative().optional(),
    reorder_threshold_ml: zod_1.z.number().nonnegative().refine(decimal3dp, 'must be 3dp'),
    supplier: zod_1.z.string().max(200).optional(),
    brand_id: id_schema_1.EntityIdSchema.optional(),
    notes: zod_1.z.string().max(2000).optional(),
    is_active: zod_1.z.boolean().optional(),
    is_visible_to_customers: zod_1.z.boolean().optional(),
    image_url: zod_1.z.string().url().optional(),
    internal_barcode: zod_1.z.string().max(64).optional(),
});
exports.APosFragranceUpdateInputSchema = exports.APosFragranceCreateInputSchema.partial().extend({
    note_classification: exports.APosFragranceNoteClassificationSchema.optional(),
    note_auto_detected: zod_1.z.boolean().optional(),
});
//# sourceMappingURL=fragrance.schema.js.map